import { useEffect, useState } from "react";
import { Banknote, FileSignature, Download, Scale } from "lucide-react";
import { api, apiError, descargarPdf } from "@/lib/api";
import { money, fecha } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

const DENOMINACIONES = [2000, 1000, 500, 200, 100, 50, 25, 10, 5, 1];

const vacio = { entregado_por: "", recibido_por: "", observaciones: "" };

/** Conteo de efectivo del turno: guarda el cierre firmable y descarga el PDF. */
export const CierreFirmado = ({ dia, efectivoSistema, usuario }) => {
  const [conteo, setConteo] = useState({});
  const [form, setForm] = useState(vacio);
  const [cierres, setCierres] = useState([]);
  const [guardando, setGuardando] = useState(false);

  const cargar = () => api.get("/caja/cierres", { params: { fecha: dia } })
    .then((r) => setCierres(r.data)).catch(() => setCierres([]));

  useEffect(() => {
    setConteo({});
    setForm({ ...vacio, entregado_por: usuario || "" });
    cargar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dia]);

  const contado = DENOMINACIONES.reduce((s, d) => s + d * (Number(conteo[d]) || 0), 0);
  const diferencia = Math.round((contado - (efectivoSistema || 0)) * 100) / 100;

  const descargar = (cid) =>
    descargarPdf(`/caja/cierres/${cid}/pdf`, {}, `cierre-de-caja-${dia}.pdf`)
      .catch((e) => toast.error(apiError(e, "No se pudo generar el PDF del cierre")));

  const guardar = async () => {
    if (contado <= 0) return toast.error("Cuente el efectivo antes de cerrar la caja");
    if (!form.entregado_por.trim()) return toast.error("Indique quién entrega el turno");
    setGuardando(true);
    try {
      const denominaciones = {};
      DENOMINACIONES.forEach((d) => {
        const c = Number(conteo[d]) || 0;
        if (c > 0) denominaciones[String(d)] = c;
      });
      const { data } = await api.post("/caja/cierres", {
        fecha: dia, denominaciones,
        entregado_por: form.entregado_por.trim(),
        recibido_por: form.recibido_por.trim() || null,
        observaciones: form.observaciones.trim() || null,
      });
      toast.success("Cierre de caja guardado", {
        description: Math.abs(data.diferencia) < 0.01 ? "El efectivo cuadra exacto"
          : data.diferencia > 0 ? `Sobrante de ${money(data.diferencia)}`
            : `Faltante de ${money(Math.abs(data.diferencia))}`,
      });
      setConteo({});
      await cargar();
      await descargar(data.id);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setGuardando(false);
    }
  };

  return (
    <section className="mt-8 rounded-lg border border-border/60 bg-card p-6 sm:p-8" data-testid="cierre-firmado">
      <div className="flex items-center gap-2">
        <FileSignature className="h-4 w-4 text-primary" />
        <h2 className="font-display text-lg font-semibold">Cierre firmado del {fecha(dia)}</h2>
      </div>
      <p className="mt-2 text-sm text-muted-foreground">
        Cuente el efectivo por denominación; el sistema calcula la diferencia y genera el
        documento con las firmas de quien entrega y quien recibe.
      </p>

      <div className="mt-6 grid gap-8 lg:grid-cols-2">
        <div>
          <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Conteo de efectivo</p>
          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            {DENOMINACIONES.map((d) => (
              <div key={d} className="flex items-center gap-3">
                <span className="w-24 shrink-0 font-mono text-sm">RD$ {d.toLocaleString()}</span>
                <Input type="number" min="0" value={conteo[d] ?? ""} className="w-20"
                       data-testid={`denominacion-${d}`}
                       onChange={(e) => setConteo({ ...conteo, [d]: e.target.value })} />
                <span className="font-mono text-xs text-muted-foreground">
                  {money(d * (Number(conteo[d]) || 0))}
                </span>
              </div>
            ))}
          </div>
        </div>

        <div className="space-y-5">
          <div className="rounded-md border border-border bg-muted/30 p-5">
            <div className="flex items-center justify-between">
              <span className="flex items-center gap-2 text-sm text-muted-foreground">
                <Banknote className="h-4 w-4" /> Efectivo contado
              </span>
              <span className="font-display text-xl font-bold" data-testid="total-contado">{money(contado)}</span>
            </div>
            <div className="mt-3 flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Según el sistema</span>
              <span className="font-mono text-sm" data-testid="efectivo-sistema">{money(efectivoSistema || 0)}</span>
            </div>
            <div className="mt-3 flex items-center justify-between border-t border-border/60 pt-3">
              <span className="flex items-center gap-2 text-sm font-semibold">
                <Scale className="h-4 w-4" /> Diferencia
              </span>
              <span className={`font-display text-lg font-bold ${
                Math.abs(diferencia) < 0.01 ? "text-primary"
                  : diferencia > 0 ? "text-accent" : "text-destructive"}`} data-testid="diferencia-caja">
                {Math.abs(diferencia) < 0.01 ? "Cuadra exacto"
                  : `${diferencia > 0 ? "Sobrante" : "Faltante"} ${money(Math.abs(diferencia))}`}
              </span>
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label>Entregado por</Label>
              <Input value={form.entregado_por} data-testid="entregado-por"
                     onChange={(e) => setForm({ ...form, entregado_por: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label>Recibido por</Label>
              <Input value={form.recibido_por} data-testid="recibido-por"
                     onChange={(e) => setForm({ ...form, recibido_por: e.target.value })} />
            </div>
          </div>
          <div className="space-y-2">
            <Label>Observaciones</Label>
            <Textarea rows={2} value={form.observaciones} data-testid="observaciones-cierre"
                      onChange={(e) => setForm({ ...form, observaciones: e.target.value })} />
          </div>
          <Button className="w-full rounded-full py-6" onClick={guardar} disabled={guardando}
                  data-testid="guardar-cierre">
            <FileSignature className="mr-2 h-4 w-4" />
            {guardando ? "Guardando…" : "Cerrar caja y descargar PDF"}
          </Button>
        </div>
      </div>

      {cierres.length > 0 && (
        <div className="mt-8 border-t border-border/60 pt-6" data-testid="historial-cierres">
          <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
            Cierres guardados de esta fecha
          </p>
          <ul className="mt-4 space-y-3">
            {cierres.map((c) => (
              <li key={c.id} className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-border/60 p-4">
                <span className="text-sm">
                  <span className="font-semibold">{money(c.efectivo_contado)}</span> contado ·
                  sistema {money(c.efectivo_sistema)} ·{" "}
                  <Badge variant={Math.abs(c.diferencia) < 0.01 ? "secondary" : "destructive"}>
                    {Math.abs(c.diferencia) < 0.01 ? "cuadra"
                      : c.diferencia > 0 ? `sobrante ${money(c.diferencia)}`
                        : `faltante ${money(Math.abs(c.diferencia))}`}
                  </Badge>
                  <span className="mt-1 block text-xs text-muted-foreground">
                    Entregó {c.entregado_por || "—"} · recibió {c.recibido_por || "—"} ·
                    registrado por {c.cerrado_por}
                  </span>
                </span>
                <Button size="sm" variant="outline" className="rounded-full"
                        onClick={() => descargar(c.id)} data-testid={`pdf-cierre-${c.id}`}>
                  <Download className="mr-2 h-3 w-3" /> PDF
                </Button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
};
